/* eslint-disable react/prop-types */
import CameraswitchIcon from "@mui/icons-material/Cameraswitch"
import CloseIcon from "@mui/icons-material/Close"
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye"
import { Box, Button, List, ListItem, useTheme } from "@mui/material"
import React, { useCallback, useEffect, useRef, useState } from "react"
export const cameraBottomStyle = {
    "@media (max-height : 600px)": {
        height: "7vh",
        bottom: "2vh",
    },
    "@media (max-height : 500px)": {
        height: "8vh",
        bottom: "3vh",
    },
    "@media (max-height : 400px)": {
        height: "9vh",
        bottom: "4vh",
    },
    "@media (max-height : 300px)": {
        height: "10vh",
        bottom: "5vh",
    },
}

interface ICustomCameraProps {
    handleClosed: () => void
    images: string[]
    setImages: React.Dispatch<React.SetStateAction<string[]>>
}

const CustomCamera: React.FC<ICustomCameraProps> = ({ handleClosed, images, setImages }) => {
    const [stream, setStream] = useState<MediaStream | null>(null)
    const theme = useTheme()
    const [capturedImages, setCapturedImages] = useState(images)
    const [open, setOpen] = useState(false)
    const [message, setMessage] = useState("")
    const [facingMode, setFacingMode] = useState<"user" | "environment">("environment")
    const videoRef = useRef<HTMLVideoElement | null>(null)

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && stream) {
                stream.getTracks().forEach((track) => track.stop())
            }
        }

        document.addEventListener("visibilitychange", handleVisibilityChange)

        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop())
            }
            document.removeEventListener("visibilitychange", handleVisibilityChange)
        }
    }, [stream])

    const initCamera = useCallback(async () => {
        try {
            const initialStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } })
            if (videoRef.current) {
                videoRef.current.srcObject = initialStream
                videoRef.current.play()
                setStream(initialStream)
            }
        } catch (error) {
            console.error("Error accessing camera:", error)
        }
    }, [facingMode])

    useEffect(() => {
        initCamera()
    }, [initCamera])

    const handleRequestPermission = async () => {
        try {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop())
            }
            const newFacingMode = facingMode === "user" ? "environment" : "user"
            setFacingMode(newFacingMode)
            const newStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: newFacingMode } })
            if (videoRef.current) {
                videoRef.current.srcObject = newStream
                videoRef.current.play()
                setStream(newStream)
            }
        } catch (error) {
            console.error("Error accessing camera:", error)
        }
    }

    const captureImage = () => {
        if (videoRef.current) {
            const canvas = document.createElement("canvas")
            canvas.width = videoRef.current.videoWidth
            canvas.height = videoRef.current.videoHeight
            const context = canvas.getContext("2d")
            if (context) {
                context.drawImage(videoRef.current, 0, 0)
                const dataURL = canvas.toDataURL("image/jpeg")
                const currentTime = new Date().toISOString()
                return { dataURL, currentTime }
            }
        }
        return null
    }

    const handleCaptureImage = () => {
        if (capturedImages.length < 5) {
            const image = captureImage()
            if (image) {
                setCapturedImages((prevImages: string[]) => [...prevImages, image.dataURL])
                setImages([...images, image.dataURL]) // Fix: Assign the new array directly to the 'images' state variable
            }
        } else {
            setMessage("Maximum 5 images")
            setOpen(true)
        }
    }

    const removeItemAtIndex = (array: string[], index: number): string[] => {
        return array.filter((_, i) => i !== index)
    }
    const handleDeleteImage = (index: number) => {
        setCapturedImages((prevImages) => prevImages.filter((_, i) => i !== index))
        setImages(removeItemAtIndex(images, index))
    }

    const prepareImages = () => {
        if (capturedImages.length > 0) {
            handleClosed()
        } else {
            setMessage("At least 1 image")
            setOpen(true)
        }
    }

    const goBackToPrev = () => {
        if (capturedImages.length > 0) {
            setOpen(true)
        } else {
            handleClosed()
        }
    }

    return (
        <Box
            width="100%"
            bgcolor={"primary.main"}
            height="100%"
            display="flex"
            justifyContent="center"
            position="absolute"
        >
            <video ref={videoRef} autoPlay style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <Box
                position="absolute"
                width="100%"
                maxWidth={700}
                display="flex"
                justifyContent="center"
                bottom="8vh"
                sx={{
                    "@media (max-height : 600px)": {
                        bottom: "9vh",
                    },
                    "@media (max-height : 500px)": {
                        bottom: "11vh",
                    },
                    "@media (max-height : 400px)": {
                        bottom: "13vh",
                    },
                    "@media (max-height : 300px)": {
                        bottom: "15vh",
                    },
                }}
            >
                <List
                    dense
                    sx={{
                        overflow: "auto",
                        "&::-webkit-scrollbar": { display: "none", width: "0px" },
                        display: "inline-flex",
                        padding: "10px",
                        flexWrap: "nowrap",
                    }}
                >
                    {capturedImages.map((image, index) => (
                        <ListItem key={index} style={{ width: 120 }}>
                            <img src={image} alt="Captured" style={{ width: 100, height: 100, borderRadius: 15 }} />
                            <CloseIcon
                                onClick={() => handleDeleteImage(index)}
                                style={{
                                    position: "relative",
                                    top: "-50%",
                                    right: "15px",
                                    color: "red",
                                    background: "white",
                                    borderRadius: "50%",
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
            <Box
                position="absolute"
                bottom="10vh"
                width="95%"
                paddingX={2}
                maxWidth={680}
                display="flex"
                justifyContent="space-around"
                height="6vh"
                sx={cameraBottomStyle}
            >
                <Button color="primary" variant="contained" onClick={goBackToPrev}>
                    Cancel
                </Button>
                <Box width="60%" display="flex" justifyContent="center" alignItems="center" zIndex={10000}>
                    <PanoramaFishEyeIcon
                        onClick={handleCaptureImage}
                        sx={{
                            color: theme.palette.common.white,
                            marginLeft: "10%",
                        }}
                    />
                    <CameraswitchIcon
                        onClick={handleRequestPermission}
                        sx={{
                            color: theme.palette.common.white,
                            marginLeft: "10%",
                        }}
                    />
                </Box>

                <Button color="primary" variant="contained" onClick={prepareImages}>
                    Next
                </Button>
            </Box>
        </Box>
    )
}

export default CustomCamera
